import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { shuffle } from 'lodash';

import RadioButton from 'components/designSystem/RadioButton/RadioButton';
import IconYesActive from 'components/shared/SVG/IconYesActive';
import IconYesInactive from 'components/shared/SVG/IconYesInactive';
import IconNoActive from 'components/shared/SVG/IconNoActive';
import IconNoInactive from 'components/shared/SVG/IconNoInactive';
import IconMaybeActive from 'components/shared/SVG/IconMaybeActive';
import IconMaybeInactive from 'components/shared/SVG/IconMaybeInactive';
import { FormContext, OrderAgain } from './ratingsSurveyModalTypes';

import { mParticleCustomEvents, mParticleEventTypes } from 'helpers/mParticle';
import { sendEvent } from './eventUtils';

import styles from './RatingsSurveyModal.module.scss';

const OA = OrderAgain;

interface OrderAgainQuestionProps {
  formContext: FormContext;
}

export default function OrderAgainQuestion({ formContext }: OrderAgainQuestionProps) {
  const router = useRouter();

  const { register, setValue, trigger, watch } = formContext;
  const questionText = 'Would you order from this restaurant again?';
  const rawChoices = [OA.Yes, OA.No, OA.Maybe];

  const selected = watch('orderAgain');

  // Only needs to be randomized on load; don't want choices re-shuffled on every render
  const choicesRef = useRef(shuffle(rawChoices));

  // Store the randomized question order in form context.  This is needed by the
  // RatingsSurveyModal component when it sends the data to mParticle.
  useEffect(() => {
    formContext.setValue('orderAgainQuestionOrder', choicesRef.current.join('-'));
  }, []);

  function iconForChoice({ value, active } = { value: OA.Maybe, active: false }) {
    if (active) {
      switch (value) {
        case OA.Yes:
          return IconYesActive;
        case OA.No:
          return IconNoActive;
        case OA.Maybe:
          return IconMaybeActive;
      }
    }

    switch (value) {
      case OA.Yes:
        return IconYesInactive;
      case OA.No:
        return IconNoInactive;
      default:
        return IconMaybeInactive;
    }
  }

  function handleChanged(value: OrderAgain): void {
    setValue('orderAgain', value);
    trigger('orderAgain');

    // Send an mParticle event anytime the customer selects a response
    sendEvent(
      router.query,
      formContext,
      mParticleCustomEvents.selectRestaurantReorderChoice,
      mParticleEventTypes.UserPreference
    );
  }

  useEffect(() => {
    register('orderAgain', { required: true });
  }, []);

  return (
    <div
      className={classNames(styles.question, styles.radioButtons)}
      data-testid="OrderAgainQuestion"
    >
      <h3 className={styles.questionText}>{questionText}</h3>
      <div className={styles.choices}>
        {choicesRef.current.map((value) => {
          const active = selected === value;

          return (
            <RadioButton
              key={value}
              name="orderAgain"
              dataTestId={`OrderAgain${value}`}
              className={styles.choice}
              icon={iconForChoice({ value, active })}
              label={value}
              value={value}
              checked={active}
              onChange={() => handleChanged(value)}
            />
          );
        })}
      </div>
    </div>
  );
}
